import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { appleAuthHelpers } from 'react-apple-signin-auth';
import ApiService from '../../services/api-service';
import { UserContext } from '../../context/userContext';
import { APPLE_CLIENT_ID, APPLE_REDIRECT_URI } from '../../constants/api';
import { getUserId } from '../../constants/function';
import cookieStorage from '../../services/cookie-storage';
import { v4 as uuidv4 } from 'uuid';
import { Images } from '../../assets/common';
import LoadingScreen from '../../components/LoadingScreen';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import './index.scss';

const fallbackLogo = require('../../assets/common/resources/SIKI_Beta.png');
const fallbackLoading = {
  icon: require('../../assets/common/resources/Sikibot.png'),
  alt: 'Loading',
};

const DesktopLanding = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isLoading, setIsLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const history = useHistory();
  const { setUser } = useContext(UserContext);
  const { getCurrentBranding, getLoadingConfig } = useAppTheme();
  const brandingDefaults = { logo: fallbackLogo, logoAlt: 'SIKI Logo' };
  const currentBranding = getCurrentBranding ? getCurrentBranding() : brandingDefaults;
  const landingBranding = {
    ...brandingDefaults,
    ...currentBranding,
  };
  const themeLoading = getLoadingConfig ? getLoadingConfig() : {};
  const loadingAssets = {
    ...fallbackLoading,
    ...themeLoading,
  };

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const navigateToSection = (section) => {
    setActiveSection(section);
    // Smooth scroll to section
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getUserProfile = async (token) => {
    try {
      const response = await ApiService.USER_PROFILE(token);
      const userData = JSON.parse(localStorage.getItem("userData"));
      userData["userData"] = response.result;
      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("isWhiteListed", response.result["isWhiteListed"]);
      
      // Keep loading state true during redirect
      // setLoginLoading(false); // Don't turn off loading here
      
      // Redirect immediately without setTimeout
      if (!response.result["gender"] || response.result["gender"] === "" || !response.result["birthYear"]) {
        // User needs to complete profile - go directly to signup
        window.location.href = '/signup';
      } else if (response.result["isWhiteListed"]) {
        // User is whitelisted and profile is complete - go directly to app
        window.location.href = '/app/home';
      } else {
        // User is not whitelisted
        window.location.href = '/WaitList';
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Keep loading during redirect
      window.location.href = '/';
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setLoginLoading(true);
    try {
      const response = await ApiService.googleSignInWithToken({
        idToken: credentialResponse.credential
      });
      
      if (response && response.result) {
        setUser(response.result);
        localStorage.setItem("userData", JSON.stringify(response.result));
        cookieStorage.createCookie("userId", getUserId());
        localStorage.setItem("token", response.result["token"]);
        localStorage.setItem("sessionId", uuidv4());
        localStorage.setItem("streamingBalance", response.result["userData"]["streamingBalance"]);
        
        cookieStorage.createCookie("Authorization", response.result["token"], 1);
        cookieStorage.createCookie("SikiAccounts", JSON.stringify(response.result["accounts"]), 1);
        cookieStorage.createCookie("swapObject", JSON.stringify(response.result["swapObject"]), 1);
        
        // Get user profile and let it handle the redirect
        await getUserProfile(response.result["token"]);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setLoginLoading(false);
    }
    // Don't set loading to false here - let the redirect handle it
  };

  const handleAppleLogin = async () => {
    setLoginLoading(true);
    try {
      const response = await appleAuthHelpers.signIn({
        authOptions: {
          clientId: APPLE_CLIENT_ID,
          scope: 'email name',
          redirectURI: APPLE_REDIRECT_URI,
          state: 'state',
          nonce: 'nonce',
          usePopup: true,
        },
        onError: (error) => console.error('Apple login error:', error),
      });
      
      if (response && response.authorization) {
        let payload;
        if (response.user) {
          payload = {
            idToken: response.authorization.id_token,
            firstName: response.user.name?.firstName,
            lastName: response.user.name?.lastName,
          };
        } else {
          payload = {
            idToken: response.authorization.id_token,
          };
        }
        
        const apiResponse = await ApiService.appleSignInWithToken(payload);
        
        if (apiResponse && apiResponse.result) {
          setUser(apiResponse.result);
          localStorage.setItem("userData", JSON.stringify(apiResponse.result));
          cookieStorage.createCookie("userId", getUserId());
          localStorage.setItem("token", apiResponse.result["token"]);
          localStorage.setItem("sessionId", uuidv4());
          localStorage.setItem("streamingBalance", apiResponse.result["userData"]["streamingBalance"]);
          
          cookieStorage.createCookie("Authorization", apiResponse.result["token"], 1);
          cookieStorage.createCookie("SikiAccounts", JSON.stringify(apiResponse.result["accounts"]), 1);
          cookieStorage.createCookie("swapObject", JSON.stringify(apiResponse.result["swapObject"]), 1);
          
          // Get user profile and let it handle the redirect
          await getUserProfile(apiResponse.result["token"]);
        }
      }
    } catch (error) {
      console.error('Apple login error:', error);
      setLoginLoading(false);
    }
    // Don't set loading to false here - let the redirect handle it
  };

  const renderContent = () => {
    return (
      <div className="desktop-sections">
        {/* Hero Section */}
        <section id="home" className="hero-section">
          <div className="hero-content">
            {/* Login Section */}
            <div className="login-section">
              <div className="login-image-container">
                <img
                  src={loadingAssets.icon}
                  alt={loadingAssets.alt || 'Loading'}
                  className="login-bot-icon"
                />
              </div>
              
              <h2>Log in</h2>
              
              {loginLoading && (
                <LoadingScreen message="Signing in..." />
              )}
              
              {!loginLoading && (
              <>
                <div className="login-buttons">
                  <div className="gradient-box">
                    <div className="login-action-btn" style={{ position: 'relative' }}>
                      <img
                        src={Images.google}
                        alt="Google"
                        className="login-icon"
                      />
                      <span>Continue with Google</span>
                      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, overflow: 'hidden' }}>
                        <GoogleLogin
                          onSuccess={(credentialResponse) => handleGoogleLogin(credentialResponse)}
                          onError={() => {
                            console.error('Google Login Failed');
                          }}
                          width="400"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="gradient-box">
                    <div className="login-action-btn" onClick={handleAppleLogin}>
                      <img
                        src={Images.apple_icon}
                        alt="Apple"
                        className="login-icon apple-icon"
                      />
                      <span>Continue with Apple</span>
                    </div>
                  </div>
                </div>
              </>
              )}
            </div>

            <div className="hero-headline-box">
              <span className="music-note left">♪</span>
              <h1 className="hero-headline">THE FUTURE OF MUSIC AND LIVE STREAMING</h1>
              <span className="music-note right">♪</span>
            </div>
            
            <div className="info-boxes">
              <div className="info-box">
                <p>The first music and live streaming platform that pays artists and curators in real time for every stream, view, tip, and purchase using blockchain based infrastructure.</p>
              </div>
              <div className="info-box">
                <p>Our ecosystem unlocks new monetization opportunities, closes the royalty gap, and brings full transparency to creators.</p>
              </div>
            </div>

            {/* App Showcase */}
            <div className="app-showcase">
              <div className="showcase-title">Download SIKI on Mobile</div>
              <div className="showcase-images">
                {(() => {
                  const hs = (landingBranding && landingBranding.homeScreens) || {};
                  const img1 = hs.app1 || require('../../assets/mobile-screenshots/screen1.jpg');
                  const img2 = hs.app2 || require('../../assets/mobile-screenshots/screen2.jpg');
                  const img3 = hs.app3 || require('../../assets/mobile-screenshots/screen3.jpg');
                  const img4 = hs.app4 || require('../../assets/mobile-screenshots/screen4.jpg');
                  return (
                    <>
                      <div className="app-screen"><img src={img1} alt="App screenshot 1" className="screenshot" /></div>
                      <div className="app-screen"><img src={img2} alt="App screenshot 2" className="screenshot" /></div>
                      <div className="app-screen"><img src={img3} alt="App screenshot 3" className="screenshot" /></div>
                      <div className="app-screen"><img src={img4} alt="App screenshot 4" className="screenshot" /></div>
                    </>
                  );
                })()}
              </div>
              <p className="showcase-note">
                Stream music, support artists, and earn rewards - all in one app
              </p>
            </div>

            {/* Features Grid */}
            <div className="features">
              <div className="feature">
                <div className="feature-icon">
                  <i className="fas fa-dollar-sign"></i>
                </div>
                <h3>Real-Time Payments</h3>
                <p>Artists get paid instantly for every stream and interaction</p>
              </div>
              <div className="feature">
                <div className="feature-icon">
                  <i className="fas fa-broadcast-tower"></i>
                </div>
                <h3>Live Streaming</h3>
                <p>The ONLY platform paying creators for every stream viewer</p>
              </div>
              <div className="feature">
                <div className="feature-icon">
                  <i className="fas fa-link"></i>
                </div>
                <h3>Blockchain Powered</h3>
                <p>Transparent, secure transactions with blockchain technology</p>
              </div>
              <div className="feature">
                <div className="feature-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h3>Full Transparency</h3>
                <p>Track earnings and analytics in real-time</p>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="about-section">
          <div className="section-content">
            <h2>About SIKI</h2>
            <div className="about-content">
              <div className="intro-box">
                <h3>Making Streams Come True</h3>
                <p>
                  SIKI is redefining music and social media by creating a platform where artists, influencers, curators, and fans 
                  earn fair and transparent royalties for every music stream. Using blockchain-enabled scaling royalties, 
                  SIKI ensures creators are paid accurately and timely.
                </p>
              </div>

              <div className="problem-solution">
                <div className="column">
                  <h3>The Problem We Solve</h3>
                  <ul className="problems">
                    <li><span className="icon">⚠️</span> Delayed royalty payments</li>
                    <li><span className="icon">⚠️</span> Lack of transparency on earnings</li>
                    <li><span className="icon">⚠️</span> Music has never been paid during a live stream</li>
                  </ul>
                </div>
                
                <div className="column">
                  <h3>Our Solution</h3>
                  <ul className="solutions">
                    <li><span className="icon">✅</span> Real-time blockchain-powered payments</li>
                    <li><span className="icon">✅</span> Full transparency with on-chain tracking</li>
                    <li><span className="icon">✅</span> Music played through SIKI in livestream is paid</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how" className="how-section">
          <div className="section-content">
            <h2>How SIKI Works</h2>
            <div className="user-types-grid">
              <div className="user-type">
                <h3>🎵 Artists & Labels</h3>
                <ul>
                  <li>Upload music and earn streaming royalties</li>
                  <li>Host live concerts and events</li>
                  <li>Earn from tips, auctions, and subscriptions</li>
                  <li>Create and sell NFTs</li>
                </ul>
              </div>
              
              <div className="user-type">
                <h3>📱 Influencers</h3>
                <ul>
                  <li>Create social media content</li>
                  <li>Earn % from streams in user-generated content</li>
                  <li>Host livestreaming events</li>
                  <li>Build engaged communities</li>
                </ul>
              </div>
              
              <div className="user-type">
                <h3>🎧 Curators</h3>
                <ul>
                  <li>Build customized playlists</li>
                  <li>Earn % from streaming royalties</li>
                  <li>Promote new artist discovery</li>
                  <li>Drive community engagement</li>
                </ul>
              </div>
              
              <div className="user-type">
                <h3>👥 Listeners</h3>
                <ul>
                  <li>Explore exclusive music</li>
                  <li>Support favorite artists directly</li>
                  <li>Get exclusive access and purchases</li>
                  <li>Earn token rewards</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="features-section">
          <div className="section-content">
            <h2>Platform Features</h2>
            <div className="features-grid">
              <div className="feature-category">
                <h3>🎵 Streaming</h3>
                <ul>
                  <li>Adaptive music & video streaming</li>
                  <li>Offline content download</li>
                  <li>Only platform to earn royalties from livestreams</li>
                </ul>
              </div>
              
              <div className="feature-category">
                <h3>💰 SIKI Token</h3>
                <ul>
                  <li>Instant payments</li>
                  <li>Marketplace transactions</li>
                  <li>Governance voting rights</li>
                </ul>
              </div>
              
              <div className="feature-category">
                <h3>🔗 Blockchain</h3>
                <ul>
                  <li>NFT minting and trading</li>
                  <li>Transparent payment tracking</li>
                  <li>On-chain royalty distribution</li>
                </ul>
              </div>
              
              <div className="feature-category">
                <h3>🎮 Gamification</h3>
                <ul>
                  <li>Level up by sharing music or growing your own</li>
                  <li>Buy and sell collectibles that level up with more song streams</li>
                  <li>Earn royalties as users listen to your studio</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };

  return (
    <div className={`desktop-landing ${isLoading ? 'loading' : 'loaded'}`}>
      {isLoading && (
        <div className="loading-screen">
          <div className="loader">
            <div className="loader-ring"></div>
            <div className="loader-ring"></div>
            <div className="loader-ring"></div>
            <img src={loadingAssets.icon} alt={loadingAssets.alt || 'Loading'} className="loader-logo" />
          </div>
        </div>
      )}

      <div className="desktop-landing__container">
        {/* Navigation Header */}
        <header className="desktop-landing__header">
          <img src={landingBranding.logo} alt={landingBranding.logoAlt || 'SIKI'} className="logo" />
          <nav className="desktop-nav">
            <button onClick={() => navigateToSection('home')} className={activeSection === 'home' ? 'active' : ''}>
              Home
            </button>
            <button onClick={() => navigateToSection('about')} className={activeSection === 'about' ? 'active' : ''}>
              About
            </button>
            <button onClick={() => navigateToSection('how')} className={activeSection === 'how' ? 'active' : ''}>
              How It Works
            </button>
            <button onClick={() => navigateToSection('features')} className={activeSection === 'features' ? 'active' : ''}>
              Features
            </button>
          </nav>
        </header>

        {/* Main Content */}
        <main className="desktop-landing__content">
          {renderContent()}
        </main>

        {/* Footer */}
        <footer className="desktop-landing__footer">
          <p className="tagline">Fueling Rhythm, Redefining Royalties</p>
          <div className="footer-links">
            <a href="/PrivacyPolicy" className="footer-link">Privacy Policy</a>
            <span className="separator">•</span>
            <a href="/TermsAndCondition" className="footer-link">Terms of Service</a>
          </div>
          <p className="copyright">© 2025 SIKI. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default DesktopLanding;
